import { useState } from "react";

/**
 * Hook that is very similar to useState but exposes the state and a mutator function that allows to mutate the state by only sending values that change.
 * @param initialState
 * @param copyConstructor function that constructs the state. Required if the state has functions the model needs access to.
 * @returns [the state, the callback mutation function]
 * @warning The same exposed setState function should not be used sequentially. Otherwise, the first setState calls could have no effect due to the batching mechanism. Furthermore, we should not destructure the exposed state when setting the state since we already doing it in the function. This can cause some unexpected behaviors.
 */
function usePartialState<TState>(
    initialState: TState | (() => TState),
    copyConstructor?: (s: TState) => TState
): [TState, (updatedFields: Partial<TState>) => TState] {
    const [state, setState] = useState<TState>(initialState);

    const properSetState = (updatedFields: Partial<TState>): TState => {
        const rawNewState = { ...state, ...updatedFields };
        const newState = copyConstructor?.(rawNewState) ?? rawNewState;
        setState(newState);
        return newState;
    };

    return [state, properSetState];
}

export { usePartialState };
